import type { IUserSignUpResponse } from '@gik/api/users/types';
import { performLogin } from '@gik/auth/utils/auth';
import type { ExternalLoginProvider } from '@gik/auth/utils/LoginProvider';
import bemBlock from '@gik/core/utils/bemBlock';
import { useCreateFlowStore } from '@gik/create';
import type { ICreateAboutFormValues } from '@gik/create/components/AboutStep/AboutStep';
import { legalDrawerLoading } from '@gik/create/components/AboutStep/AboutStep';
import type { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import React from 'react';
import { SignInFlowContent } from '@gik/auth/components/SignInFlow/SignInFlowContent';
import type { IForgotPasswordFormValues } from '@gik/auth/components/ForgotPassword/ForgotPasswordForm';
import { openForgotPasswordModal } from '@gik/auth/utils/authModals';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import i18n from '@gik/i18n';
import { translationKeys } from '../../i18n/en';
import { Button } from '@gik/ui/Button';

export interface IFinishStepProps {
  onSubmit?: (values: Pick<ICreateAboutFormValues, AboutStepInputNames.Email>) => void;
  onSuccess?: (provider?: ExternalLoginProvider) => void;
  trackTermsOfService?: () => void;
  trackPrivacyPolicy?: () => void;
}

const blockName = 'finish-step';

export default function FinishStep({ onSuccess, trackTermsOfService, trackPrivacyPolicy }: IFinishStepProps) {
  const bem = bemBlock(blockName);
  // prettier-ignore

  const url = new URL(window.location.toString());
  url.searchParams.set('finish', 'true');
  const formValues = useCreateFlowStore(state => state.formValues);

  function handleSuccess(response: IUserSignUpResponse) {
    performLogin(
      response.userId,
      response.refreshToken,
      response.refreshTokenHash,
      response.walletAccessToken,
      response.redirectUrl
    );
    onSuccess?.();
  }

  const { openLegalTextDrawer } = useCreateFlowStore(state => ({
    openLegalTextDrawer: state.openLegalTextDrawer,
  }));

  function handleClickOpenTOS() {
    trackTermsOfService?.();
    openLegalTextDrawer(legalDrawerLoading, legalDrawerLoading, 'termsOfService');
  }

  function handleClickOpenPP() {
    trackPrivacyPolicy?.();
    openLegalTextDrawer(legalDrawerLoading, legalDrawerLoading, 'privacyPolicy');
  }

  return (
    <div className={bem(null, null, ['tw-p-8', 'tw-pt-16'])}>
      <SignInFlowContent
        copyVariant={SignInFlowContentCopyVariant.CREATE_PAGE}
        createPageInkindName={formValues.pageName}
        onSuccess={handleSuccess}
        onForgotPassword={async (initialValues?: Partial<IForgotPasswordFormValues>) => {
          openForgotPasswordModal(initialValues);
        }}
        hideTOS
      />

      <p
        className={bem('terms-disclaimer', null, [
          'tw-font-normal tw-text-sm tw-leading-tight tw-text-center tw-mt-4 tw-italic',
        ])}
      >
        {i18n.t(translationKeys.TermsDisclaimer1).toString()}{' '}
        <Button onClick={handleClickOpenTOS} variant="primary-link">
          {i18n.t(translationKeys.TermsDisclaimer2).toString()}
        </Button>{' '}
        {i18n.t(translationKeys.TermsDisclaimer3).toString()}{' '}
        <Button onClick={handleClickOpenPP} variant="primary-link">
          {i18n.t(translationKeys.TermsDisclaimer4).toString()}
        </Button>
        {'. '}
        {i18n.t(translationKeys.TermsDisclaimer5).toString()}
      </p>
    </div>
  );
}
